import { PencilIcon, ShareIcon } from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import ContextButton from "@via/components/context-button/context-button.tsx";
import { GenericError } from "@via/components/error.tsx";
import { ExecutionVisibilityBadge } from "@via/components/execution-visibility-badge.tsx";
import {
  SkeletonLoader,
  Spinner,
  TitleLoader,
} from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  useBeforeUnload,
  useNavigationBlocker,
} from "@via/hooks/useBeforeUnload.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import RunAdvanced from "@via/pages/projects/runs/advanced/advanced.tsx";
import ChangeProjectDuplicateRunModal from "@via/pages/projects/runs/change-project-duplicate-run-modal";
import DeleteRunModal from "@via/pages/projects/runs/delete-run-modal.tsx";
import TerminateWithoutConfirmationModal from "@via/pages/projects/runs/terminate-without-confirmation-modal.tsx";
import { type UnconfirmedTermination } from "@via/pages/projects/runs/terminate-refusal";
import ManageRunAttemptsModal from "@via/pages/projects/runs/manage-run-attempts-modal.tsx";
import RunSettings from "@via/pages/projects/runs/overview/run-settings.tsx";
import { shouldShowPremiumRunUpsell } from "@via/pages/projects/runs/premium-run";
import PremiumRunModal from "@via/pages/projects/runs/premium-run-modal.tsx";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link.tsx";
import RunReports from "@via/pages/projects/runs/reports/reports.tsx";
import RunDownloads from "@via/pages/projects/runs/downloads/downloads.tsx";
import { RunTestTab } from "@via/pages/projects/runs/run-test-tab.tsx";
import RunLogs from "@via/pages/projects/runs/run-logs/run-logs.tsx";
import {
  useInitiateRun,
  usePipelineRunDetails,
  useRunAttempts,
  useRunDetails,
  useRunEstimationsByRunId,
  useRunStatus,
  useSaveRun,
  useTerminateRun,
  useGetRunDuplicationStatus,
  useDownloadRunLogs,
  useDownloadRunParams,
  useDownloadRunConfig,
} from "@via/pages/projects/runs/runs.api.tsx";
import LaunchRefusalPanel from "@via/pages/projects/runs/launch-refusal-panel.tsx";
import {
  useLaunchRefusal,
  type LaunchRef,
} from "@via/pages/projects/runs/use-launch-refusal";
import Settings from "@via/pages/projects/runs/settings/settings.tsx";
import ShareRunModal from "@via/pages/projects/runs/share-run-modal.tsx";
import {
  getRunStatusStyle,
  getRunStatusText,
  getUnsatisfiedInputGroups,
  isPipelineExternal,
  resolveRunActionStatus,
  type InputNode,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  ERROR,
  INITIATE_NEW_RUN_BUTTON,
  INITIATE_RUN_BUTTON,
  RUN_MANAGEMENT_MODALS,
  RunTabs,
  TERMINATE_RUN_BUTTON,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { SaveIcon } from "@via/utils/custom-icons";
import {
  classNames,
  convertObjValuesToString,
  formatDateString,
  isFalsyOrEmpty,
  isObject,
  isObjectEmpty,
} from "@via/utils/helpers.tsx";
import {
  type SelectOption,
  type TableContextMenuInterface,
} from "@via/utils/interfaces.tsx";
import {
  InputType,
  RunStatus,
  RunType,
  type SaveRunInputs,
  DuplicationStatus,
  ExecutorJobsType,
  type RunEnvironmentAvailableListItem,
  activeRunStatuses,
  canLaunchNewAttempt,
  normalizeRunStatus,
} from "common";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { useForm } from "react-hook-form";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "./run.css";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";

export default function Run(): JSX.Element {
  const { runId = "" } = useParams();
  const { setSelectedProject } = useGlobal();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { user, config } = useAuth();
  const measureRef = useRef<HTMLSpanElement>(null);
  const runNameInputRef = useRef<HTMLInputElement>(null);
  const [isRunDownloading, setIsRunDownloading] = useState<boolean>(false);
  const [runNameInputWidth, setRunNameInputWidth] = useState<number>(60);
  const [runAttempts, setRunAttempts] = useState<SelectOption[]>([]);
  const [selectedRunAttempt, setSelectedRunAttempt] = useState<SelectOption>();
  // 3G-3a: the launch refusal panel, the unsaved environment switch and launch identity (spec 5.3, 5.4). Declared
  // before useInitiateRun so the mutation's onRefused is this page's identity check.
  const launchRefusal = useLaunchRefusal({
    runId,
    attemptId: String(selectedRunAttempt?.value ?? ""),
  });
  // The identity of the launch in flight, captured in requestRunLaunch and handed to initiateRun by the effect
  // below. The 389 auto retry re-enters through onSubmit and the same effect, so it carries the same ref (D14).
  const launchRefRef = useRef<LaunchRef | null>(null);
  const {
    mutate: saveRun,
    isPending: isSaveRunLoading,
    isSuccess: isSaveRunSuccess,
  } = useSaveRun();
  const {
    mutate: initiateRun,
    error: runInitiationError,
    isPending: isRunInitiationLoading,
    isError: isRunInitiationError,
    isSuccess: isRunInitiationSuccess,
  } = useInitiateRun({ onRefused: launchRefusal.acceptRefusal });
  const [unconfirmedTermination, setUnconfirmedTermination] =
    useState<UnconfirmedTermination | null>(null);
  const { mutate: terminateRun, isPending: isTerminateRunLoading } =
    useTerminateRun({ onUnconfirmed: setUnconfirmedTermination });
  const [runName, setRunName] = useState<string>("");
  const [isEditingRunName, setIsEditingRunName] = useState(false);
  const [isShareRunModalOpen, setIsShareRunModalOpen] = useState(false);
  const {
    mutate: downloadRunLogs,
    data: runLogsDownloadResponse,
    isSuccess: isRunLogsDownloadSuccess,
    isPending: isRunLogsDownloadPending,
    reset: resetRunLogsDownload,
  } = useDownloadRunLogs();

  const {
    mutate: downloadRunParams,
    data: runParamsDownloadResponse,
    isSuccess: isRunParamsDownloadSuccess,
    isPending: isRunParamsDownloadPending,
    reset: resetRunParamsDownload,
  } = useDownloadRunParams();

  const {
    mutate: downloadRunConfig,
    data: runConfigDownloadResponse,
    isSuccess: isRunConfigDownloadSuccess,
    isPending: isRunConfigDownloadPending,
    reset: resetRunConfigDownload,
  } = useDownloadRunConfig();

  const {
    data: runStatus,
    isLoading: isRunStatusLoading,
    refetch: refetchRunStatus,
  } = useRunStatus(runId, selectedRunAttempt?.value);
  const {
    data: runSettingsData,
    isLoading: isRunSettingsLoading,
    isSuccess: isRunSettingsSuccess,
    isError: isRunSettingsError,
    refetch: refetchRunSettings,
  } = useRunDetails(runId, selectedRunAttempt?.value);
  const {
    data: runAttemptsData,
    isLoading: isRunAttemptsLoading,
    isSuccess: isRunAttemptsSuccess,
  } = useRunAttempts(runId);

  const isCostPredictionAllowed = !!user?.allowedActions?.allowCostPrediction;
  const { data: runEstimationsData } = useRunEstimationsByRunId(
    runId,
    isCostPredictionAllowed
  );

  const totalPredictedCost = useMemo(() => {
    const cost = runEstimationsData?.[0]?.totalCost;
    return typeof cost === "number" ? cost : null;
  }, [runEstimationsData]);

  const shouldShowPredictedCost =
    isCostPredictionAllowed &&
    typeof totalPredictedCost === "number" &&
    totalPredictedCost > 0;

  const formattedPredictedCost = useMemo(() => {
    if (!shouldShowPredictedCost || totalPredictedCost === null) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalPredictedCost);
  }, [shouldShowPredictedCost, totalPredictedCost]);
  const {
    data: pipelineRunDetails,
    isLoading: isPipelineRunDetailsLoading,
    isError: isPipelineRunDetailsError,
    error: pipelineRunDetailsError,
  } = usePipelineRunDetails(runId);
  const { setNotification, NOTIFICATION } = useNotifications();
  const [paramsInputs, setParamsInputs] = useState<SaveRunInputs[]>([]);
  const [shouldInitiateRun, setShouldInitiateRun] = useState<RunType | null>(
    null
  );
  const [requiredGlobalInputs, setRequiredGlobalInputs] = useState<InputNode[]>(
    []
  );
  // The pipeline's data entry points (@file inputs). Deliberately NOT part of
  // requiredGlobalInputs: a pipeline offering alternatives has to mark them all
  // @optional, so they can only be warned about, not required.
  const [dataInputs, setDataInputs] = useState<InputNode[]>([]);
  // The pipeline's @required_one_of groups: each needs at least one member
  // filled before the run can launch (#443). Empty for every pipeline that
  // declares none, which leaves the gate exactly as it was.
  const [requiredInputGroups, setRequiredInputGroups] = useState<InputNode[][]>(
    []
  );
  const [isDeleteRunModalOpen, setIsDeleteRunModalOpen] = useState(false);
  const [isManageRunAttemptsModalOpen, setIsManageRunAttemptsModalOpen] =
    useState(false);
  const [isChangeProjectModalOpen, setIsChangeProjectModalOpen] =
    useState(false);
  const [isDuplicateRunModalOpen, setIsDuplicateRunModalOpen] = useState(false);

  // Check for active duplication status
  const { data: duplicationStatus } = useGetRunDuplicationStatus(runId);

  const showDuplicationBanner =
    duplicationStatus &&
    duplicationStatus.status === DuplicationStatus.InProgress;
  const [displayWarnings, setDisplayWarnings] = useState(false);
  const [runLogsContent, setRunLogsContent] = useState<
    ReactNode | string | null
  >(null);
  // True from the moment a run launch is requested until the launch call settles.
  // Saving and initiating take a few seconds, and until they finish the logs API
  // still returns the PREVIOUS attempt — so the log panel must show its loading
  // state rather than the old attempt's output.
  //
  // It also spans the gap between the two launch mutations: saving resolves before
  // the effect below fires the initiate call, so for one painted render neither
  // mutation is pending. Without this flag the action button fell back to the
  // stale server status there and flashed the previous attempt's "Run Error".
  const [isLaunchInFlight, setIsLaunchInFlight] = useState(false);
  const [activeRunTab, setActiveRunTab] = useState<RunTabs>(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && Object.values(RunTabs).includes(tabParam as RunTabs)) {
      return tabParam as RunTabs;
    }
    return RunTabs.RunSettings;
  });
  const [isPremiumRunModalOpen, setIsPremiumRunModalOpen] = useState(false);
  const { changeCount, increaseChangeCount, resetChangeCount } =
    useChangesDetect();

  // Normalised, not compared raw. A run row written before the 2026-07-29
  // vocabulary rename stores `Aborted` rather than `Unreachable`, and a bare
  // `===` against those values is silently false.
  const canonicalRunStatus = normalizeRunStatus(runStatus?.runStatus ?? "");

  const isRunStatusActive =
    !!canonicalRunStatus && activeRunStatuses.includes(canonicalRunStatus);

  const isAnyOldRunAttempSelected = !!(
    selectedRunAttempt?.value &&
    selectedRunAttempt?.value !== runAttempts[0]?.value
  );

  const isUserViewer =
    user?.userRole !== USER_ROLES.ADMIN &&
    runSettingsData?.owner?.username !== user?.username;

  const isPremiumRunButton = shouldShowPremiumRunUpsell({
    enableStripe: config?.ENABLE_STRIPE,
    availableEnvironmentCount:
      runSettingsData?.runEnvironment?.availableList?.length,
    isRunOwner: runSettingsData?.owner?.username === user?.username,
  });

  const form = useForm({
    mode: "onChange",
  });

  const {
    reset,
    formState: { dirtyFields },
    setValue,
  } = form;

  useEffect(() => {
    if (isPipelineRunDetailsError) {
      setNotification({
        id: "fetch-pipeline-run-details-failed",
        message: "Run input details could not be retrieved",
        details:
          pipelineRunDetailsError?.response?.data?.message ??
          "Run input details could not be retrieved",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [isPipelineRunDetailsError, pipelineRunDetailsError]);

  useEffect(() => {
    if (isRunSettingsSuccess) {
      const { inputs, name, project, mainPipeline } = runSettingsData;
      setRunName(name);
      setSelectedProject(project);

      if (!isPipelineExternal(mainPipeline.pipelineType)) {
        // If serviceTokenId is present, set the value to the service token id.
        const normalized = (inputs ?? []).map(inp => {
          if (
            (inp.value == null || inp.value === "") &&
            inp.serviceTokenId != null
          ) {
            return { ...inp, value: String(inp.serviceTokenId) };
          }
          return inp;
        });
        setParamsInputs(normalized);
      }
    }
  }, [runSettingsData, isRunSettingsSuccess]);

  useEffect(() => {
    if (!isSaveRunLoading && isSaveRunSuccess && shouldInitiateRun) {
      initiateRun({
        runId,
        runType: shouldInitiateRun,
        launchRef: launchRefRef.current ?? undefined,
      });
      setRunLogsContent(null);
      setActiveRunTab(RunTabs.Logs);
      setShouldInitiateRun(null);
    }
    if (isSaveRunSuccess) {
      resetChangeCount();
    }
  }, [
    isSaveRunLoading,
    isSaveRunSuccess,
    shouldInitiateRun,
    initiateRun,
    runId,
  ]);

  // Release the log panel once the launch call settles. On success the cached
  // logs for this run have been dropped, so the panel loads the new attempt from
  // scratch; on failure the previous attempt is still the current one, so showing
  // its logs again is correct.
  useEffect(() => {
    if (isRunInitiationSuccess || isRunInitiationError) {
      setIsLaunchInFlight(false);
    }
  }, [isRunInitiationSuccess, isRunInitiationError]);

  // A successful launch clears the panel (spec 5.4, "Clearing"). beginLaunch already cleared it on submit;
  // this covers a refusal that was accepted between the two.
  useEffect(() => {
    if (isRunInitiationSuccess) launchRefusal.noteLaunchSucceeded();
  }, [isRunInitiationSuccess, launchRefusal.noteLaunchSucceeded]);

  // The pending selection is persisted once a run details response carries it. Child effects run first, so
  // settings.tsx has already applied the guard to this same response by the time this releases it.
  useEffect(() => {
    if (isRunSettingsSuccess) {
      launchRefusal.reconcileWithFetched(
        runSettingsData?.runEnvironment?.selectedId ?? null
      );
    }
  }, [
    isRunSettingsSuccess,
    runSettingsData,
    launchRefusal.reconcileWithFetched,
  ]);

  useEffect(() => {
    if (isSaveRunSuccess) {
      reset({}, { keepValues: true });
    }
  }, [isSaveRunSuccess, reset]);

  useEffect(() => {
    if (!isRunAttemptsSuccess) return;

    if (runAttemptsData?.attempts.length > 0) {
      const options = runAttemptsData.attempts.map(
        ({ id, name, dateCreated }, index) => ({
          label: name ?? `Attempt ${runAttemptsData.attempts.length - index}`,
          value: id,
          dateCreated,
        })
      );
      options.unshift({
        label: "Current Attempt",
        value: "",
        dateCreated: "",
      });
      setSelectedRunAttempt(options[0]);
      setRunAttempts(options);
    } else {
      setSelectedRunAttempt(undefined);
      setRunAttempts([]);
    }
  }, [runAttemptsData, isRunAttemptsSuccess]);

  useEffect(() => {
    if (!isRunLogsDownloadSuccess || !runLogsDownloadResponse) return;

    const blob = runLogsDownloadResponse.data;
    const sanitizedRunName = (runSettingsData?.name || "run")
      .replace(/[^\w\d\-_. ]+/g, "_")
      .trim();
    const fallbackFileName = sanitizedRunName
      ? `${sanitizedRunName}.zip`
      : "run.zip";
    const downloadFileName =
      runLogsDownloadResponse.headers?.["x-download-filename"] ||
      fallbackFileName;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    resetRunLogsDownload();
  }, [
    isRunLogsDownloadSuccess,
    runLogsDownloadResponse,
    runSettingsData?.name,
    resetRunLogsDownload,
  ]);

  useEffect(() => {
    if (!isRunParamsDownloadSuccess || !runParamsDownloadResponse) return;

    const blob = runParamsDownloadResponse.data;
    const sanitizedRunName = (runSettingsData?.name || "run")
      .replace(/[^\w\d\-_. ]+/g, "_")
      .trim();
    const fallbackFileName = sanitizedRunName
      ? `${sanitizedRunName}.json`
      : "run.json";
    const downloadFileName =
      runParamsDownloadResponse.headers?.["x-download-filename"] ||
      fallbackFileName;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    resetRunParamsDownload();
  }, [
    isRunParamsDownloadSuccess,
    runParamsDownloadResponse,
    runSettingsData?.name,
    resetRunParamsDownload,
  ]);

  useEffect(() => {
    if (!isRunConfigDownloadSuccess || !runConfigDownloadResponse) return;

    const blob = runConfigDownloadResponse.data;
    const sanitizedRunName = (runSettingsData?.name || "run")
      .replace(/[^\w\d\-_. ]+/g, "_")
      .trim();
    const fallbackFileName = sanitizedRunName
      ? `${sanitizedRunName}-config.json`
      : "run-config.json";
    const downloadFileName =
      runConfigDownloadResponse.headers?.["x-download-filename"] ||
      fallbackFileName;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    resetRunConfigDownload();
  }, [
    isRunConfigDownloadSuccess,
    runConfigDownloadResponse,
    runSettingsData?.name,
    resetRunConfigDownload,
  ]);

  const handleRunAttemptChange = (selected): void => {
    if (selected?.value !== selectedRunAttempt?.value) {
      const shouldNavigate = navigate(pathname, () => {
        setSelectedRunAttempt(selected);
        resetChangeCount();
        reset();
      });
      if (!shouldNavigate) {
        setSelectedRunAttempt(prev => ({
          ...prev,
          label: prev?.label ?? "",
          value: prev?.value ?? null,
        }));
        return;
      }
    }
  };

  useEffect(() => {
    if (selectedRunAttempt) {
      refetchRunStatus();
      refetchRunSettings();
    }
  }, [selectedRunAttempt, refetchRunStatus, refetchRunSettings]);

  useEffect(() => {
    const updateWidth = () => {
      if (measureRef?.current) {
        const measuredWidth = measureRef.current?.offsetWidth;
        const newWidth = Math.max(60, Math.min(measuredWidth + 30, 320)); // min 50px, max-w-80
        setRunNameInputWidth(newWidth);
      }
    };

    updateWidth();
  }, [runName, measureRef?.current]);

  const runContextMenuOptions = useMemo(() => {
    const options: TableContextMenuInterface[] = [];
    // owner or admin
    // anyone who can see the run
    if (runSettingsData?.owner?.username) {
      options.push({ name: "Duplicate Run", href: "#", id: "duplicate-run" });
      options.push({
        name: "Download Run Logs",
        href: "#",
        id: "download-run",
        disabled: isRunLogsDownloadPending,
      });
      options.push({
        name: "Download Run Params",
        href: "#",
        id: "download-run-parameters",
        disabled: isRunParamsDownloadPending,
      });
      options.push({
        name: "Download Run Config",
        href: "#",
        id: "download-run-config",
        disabled: isRunConfigDownloadPending,
      });
    }
    if (
      runSettingsData?.owner?.username === user?.username ||
      user?.userRole === USER_ROLES.ADMIN
    ) {
      options.push({
        name: "Manage Run Attempts",
        href: "#",
        id: "manage-run-attempts",
      });
      options.push({ name: "Change Project", href: "#", id: "change-project" });
      options.push({ name: "Delete Run", href: "#", id: "delete-run" });
    }
    return options;
  }, [
    runSettingsData?.owner?.username,
    user,
    isRunLogsDownloadPending,
    isRunParamsDownloadPending,
    isRunConfigDownloadPending,
  ]);

  const isInitiateRunDisable =
    !form.getValues("launchDirectory") ||
    !form.getValues("runEnvironmentId") ||
    isNaN(Number(form.getValues("runEnvironmentId"))) ||
    requiredGlobalInputs.filter(({ id }) => isFalsyOrEmpty(form.getValues(id)))
      .length > 0 ||
    getUnsatisfiedInputGroups(requiredInputGroups, id => form.getValues(id))
      .length > 0;

  const runActionStatus = resolveRunActionStatus({
    isMutationPending:
      isSaveRunLoading || isRunInitiationLoading || isTerminateRunLoading,
    isLaunchRequested: isLaunchInFlight,
    isInitiateRunDisable,
    isAnyOldRunAttempSelected,
    runStatus: runStatus?.runStatus,
  });

  const message = "You have unsaved changes, do you really want to leave?";
  const hasActiveDownload =
    isRunDownloading ||
    isRunLogsDownloadPending ||
    isRunParamsDownloadPending ||
    isRunConfigDownloadPending;
  const shouldWarn = !isObjectEmpty(dirtyFields) && !hasActiveDownload;
  useBeforeUnload(shouldWarn, message);
  const { navigate, NavigationBlockerModal } = useNavigationBlocker(
    shouldWarn || changeCount > 0,
    []
  );

  const handleEditRunName = () => {
    increaseChangeCount();
    setIsEditingRunName(true);
    runNameInputRef.current?.focus();
  };

  const handleBlurRunName = () => {
    setIsEditingRunName(false);
  };

  const handleSaveRun = (payload: Record<string, unknown>): void => {
    const {
      params,
      pipe,
      launchDirectory,
      runEnvironmentId,
      summary,
      useDocker,
      useSingularity,
      dockerConfig,
      singularityConfig,
      nextflowVersion,
      publishDir,
      workDirectory,
      execAllCheck,
      execAll,
      execEachCheck,
      execEach,
      deleteIntermediateCheck,
      deleteIntermediateDelay,
      reportOptions,
      groupId,
      permission,
      hostname, // exclude hostname from payload
      selectedRunEnvironment, // exclude selectedRunEnvironment from payload
      selectedRunEnvironmentUpdated, // exclude selectedRunEnvironmentUpdated from payload
      updateAutofillAfterSelectedRunEnvironment, // exclude updateAutofillAfterSelectedRunEnvironment from payload
      ...rest
    } = payload;

    const isExternalPipeline = isPipelineExternal(
      runSettingsData.mainPipeline.pipelineType
    );
    const inputs = isExternalPipeline
      ? JSON.parse(JSON.stringify(params))
      : params
        ? Object.entries(params as Record<string, unknown>)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => {
              const input = paramsInputs.find(input => input.name === key);
              if (!input) {
                return {
                  name: key,
                  value,
                  type: InputType.Input,
                };
              } else {
                return {
                  ...input,
                  value,
                };
              }
            })
        : undefined;

    const data = {
      processOptions: {
        pipe: isObject(pipe) ? convertObjValuesToString(pipe) : {},
        ...convertObjValuesToString(rest),
      },
      runEnvironmentId,
      name: runName,
      summary,
      launchDirectory: (launchDirectory as string).replace(/\s+/g, ""),
      inputs,
      publishDir,
      workDirectory: typeof workDirectory === "string" ? workDirectory : "",
      nextflowVersion:
        typeof nextflowVersion === "string" ? nextflowVersion : "",
      execAllCheck,
      ...(isObject(execAll) &&
        !isObjectEmpty(execAll) && {
          execAll,
        }),
      deleteIntermediateCheck,
      deleteIntermediateDelay: isNaN(
        parseInt(deleteIntermediateDelay as string, 10)
      )
        ? undefined
        : parseInt(deleteIntermediateDelay as string, 10),
      reportOptions,
      groupId,
      permission,
      // Only include these fields if the pipeline is not external
      ...(!isExternalPipeline && {
        useDocker,
        useSingularity,
        dockerConfig,
        singularityConfig,
        execEachCheck,
        ...(isObject(execEach) &&
          !isObjectEmpty(execEach) && {
            execEach: {
              ...Object.fromEntries(
                Object.entries(execEach).map(([key, value]) => [
                  key,
                  {
                    ...(value as Record<string, unknown>),
                  },
                ])
              ),
            },
          }),
      }),
    };
    saveRun({
      id: runId,
      payload: data,
    });
  };

  const onSubmit = form.handleSubmit(handleSaveRun);

  useEffect(() => {
    if (isRunInitiationError && runInitiationError && isRunSettingsSuccess) {
      const reRunIfRetryRequires = async () => {
        await onSubmit();
        setShouldInitiateRun(RunType.NewRun);
      };

      if (runInitiationError.response?.status === 389) {
        setValue("selectedRunEnvironmentUpdated", true, { shouldDirty: true });
        // Small delay to let react-hook-form update internal state
        setTimeout(() => {
          reRunIfRetryRequires();
        }, 1000);
      }
    }
  }, [isRunInitiationError, runInitiationError, isRunSettingsSuccess]);

  // Drop the previous attempt's logs the instant a launch is requested. The save
  // and initiate round-trips take a few seconds, and until they complete the logs
  // endpoint still returns the old attempt — so clearing only after the save (as
  // before) left the stale output on screen for that whole window.
  const requestRunLaunch = async (runType: RunType): Promise<void> => {
    setRunLogsContent(null);
    setIsLaunchInFlight(true);
    // Identity is captured here, once per logical launch, from the value the save is about to persist.
    launchRefRef.current = launchRefusal.beginLaunch(
      Number(form.getValues("runEnvironmentId")) || null
    );
    await onSubmit();
    setShouldInitiateRun(runType);
  };

  const handleRunAction = async (
    action: TableContextMenuInterface
  ): Promise<void> => {
    switch (action.name) {
      case "Start":
        await requestRunLaunch(RunType.NewRun);
        break;
      case "ReRun":
        await requestRunLaunch(RunType.ReRun);
        break;
      case "Resume":
        await requestRunLaunch(RunType.ResumeRun);
        break;
      case "Terminate":
        await terminateRun({ id: runId });
        break;
      case "Delete Run":
        setIsDeleteRunModalOpen(true);
        break;
      case "Manage Run Attempts":
        setIsManageRunAttemptsModalOpen(true);
        break;
      case "Change Project":
        setIsChangeProjectModalOpen(true);
        break;
      case "Duplicate Run":
        setIsDuplicateRunModalOpen(true);
        break;
      case "Download Run Logs":
        if (!isRunLogsDownloadPending && runId) {
          downloadRunLogs({
            runId,
            selectedRunAttempt: selectedRunAttempt,
            runAttempts: runAttempts,
          });
        }
        break;
      case "Download Run Params":
        if (!isRunParamsDownloadPending && runId) {
          downloadRunParams({
            runId,
            selectedRunAttempt: selectedRunAttempt,
            runAttempts: runAttempts,
          });
        }
        break;
      case "Download Run Config":
        if (!isRunConfigDownloadPending && runId) {
          downloadRunConfig({
            runId,
            selectedRunAttempt: selectedRunAttempt,
            runAttempts: runAttempts,
          });
        }
        break;
      default:
        break;
    }
  };

  // Conditionally add Downloads tab if enabled and executor job is cloud or publish dir is a cloud path
  const selectedRunEnvironment =
    runSettingsData?.runEnvironment?.availableList?.find(
      (env: RunEnvironmentAvailableListItem) =>
        env.id === runSettingsData?.runEnvironment?.selectedId
    );
  const executorJob = selectedRunEnvironment?.executorJob;
  const isCloudExecutor =
    executorJob === ExecutorJobsType.AwsBatch ||
    executorJob === ExecutorJobsType.GoogleBatch;
  const resolvedPublishDir =
    runSettingsData?.advancedSettings?.publishDir?.resolvedReportPath?.trim() ??
    "";
  const hasCloudPublishDir =
    resolvedPublishDir.startsWith("s3://") ||
    resolvedPublishDir.startsWith("gs://");
  const isDownloadsTabEnabled =
    !!config?.ENABLE_RUN_DOWNLOADS && (isCloudExecutor || hasCloudPublishDir);

  useEffect(() => {
    if (activeRunTab === RunTabs.Downloads && !isDownloadsTabEnabled) {
      setActiveRunTab(RunTabs.Reports);
    }
  }, [activeRunTab, isDownloadsTabEnabled]);

  // Conditional for the same reason Downloads is: a tab that renders nothing usable for every
  // ordinary pipeline run teaches people the feature is broken. It is gated on the run being a
  // standalone PROCESS run, not on that process already having expected results: gating on the
  // latter would make Capture unreachable, because you cannot create a baseline through a tab
  // that only appears once a baseline exists.
  const testTabProcessId = runSettingsData?.mainPipeline?.sourceProcessId;
  const isTestTabEnabled =
    runSettingsData?.mainPipeline?.subjectType === "process" &&
    Boolean(testTabProcessId);

  useEffect(() => {
    if (activeRunTab === RunTabs.Test && !isTestTabEnabled) {
      setActiveRunTab(RunTabs.Reports);
    }
  }, [activeRunTab, isTestTabEnabled]);

  // 3G-3a. transport knowledge stays here: the panel receives the SSH environments already filtered.
  const availableRunEnvironments: RunEnvironmentAvailableListItem[] =
    runSettingsData?.runEnvironment?.availableList ?? [];
  const sshRunEnvironments = availableRunEnvironments
    .filter(env => env.transport === "ssh")
    .map(({ id, name }) => ({ id, name }));

  // The switch changes the selection and moves the page to Run Settings, where the selector is; Launch left it
  // on Logs. Nothing is submitted: no initiate request leaves the page until the user clicks Launch (D2).
  const handleSwitchEnvironment = (id: number): void => {
    const environment = availableRunEnvironments.find(env => env.id === id);
    if (!environment) return;
    setValue("runEnvironmentId", id, { shouldDirty: true });
    setActiveRunTab(RunTabs.RunSettings);
    launchRefusal.switchEnvironment(
      { id, name: environment.name },
      "environment-selector"
    );
  };

  const handleOpenEnvironmentPicker = (): void => {
    setActiveRunTab(RunTabs.RunSettings);
    launchRefusal.openPicker();
  };

  // A pick through the picker is the fallback switch with a choice step, so it protects the refetch the same
  // way and confirms in the panel; a pick the user made on their own clears everything (spec 5.3, 5.4).
  const handleEnvironmentChange = (
    environment: { id: number; name: string },
    source: "picker" | "user"
  ): void => {
    if (source === "picker") {
      launchRefusal.switchEnvironment(environment, "panel-confirmation");
    } else {
      launchRefusal.noteUserEnvironmentChange();
    }
  };

  const handleEnvironmentPickerClosed = ({
    picked,
  }: {
    picked: boolean;
  }): void => {
    if (!picked) launchRefusal.cancelPicker();
  };

  const runTabs = [
    {
      name: RunTabs.RunSettings,
      children:
        isRunSettingsLoading || isPipelineRunDetailsLoading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isRunSettingsError || isPipelineRunDetailsError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <RunSettings
            form={form}
            paramsInputs={paramsInputs}
            setParamsInputs={setParamsInputs}
            requiredGlobalInputs={requiredGlobalInputs}
            setRequiredGlobalInputs={setRequiredGlobalInputs}
            dataInputs={dataInputs}
            setDataInputs={setDataInputs}
            requiredInputGroups={requiredInputGroups}
            setRequiredInputGroups={setRequiredInputGroups}
            displayWarnings={!isAnyOldRunAttempSelected && displayWarnings}
            pipelineRunDetails={pipelineRunDetails}
            runSettingsData={runSettingsData}
            isRunSettingsLoading={isRunSettingsLoading}
            isRunSettingsSuccess={isRunSettingsSuccess}
            isRunSettingsError={isRunSettingsError}
            isSaveRunSuccess={isSaveRunSuccess}
            readOnly={isAnyOldRunAttempSelected || isUserViewer}
            activeRunTab={activeRunTab}
            pendingRunEnvironmentId={launchRefusal.pendingSelection?.id ?? null}
            environmentPicker={launchRefusal.pickerFilter}
            onEnvironmentChange={handleEnvironmentChange}
            onEnvironmentPickerClosed={handleEnvironmentPickerClosed}
          />
        ),
    },
    {
      name: RunTabs.Advanced,
      children:
        isRunSettingsLoading || isPipelineRunDetailsLoading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isRunSettingsError || isPipelineRunDetailsError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <RunAdvanced
            form={form}
            pipelineRunDetails={pipelineRunDetails}
            runSettingsData={runSettingsData}
            isRunSettingsLoading={isRunSettingsLoading}
            isRunSettingsSuccess={isRunSettingsSuccess}
            isRunSettingsError={isRunSettingsError}
            readOnly={isAnyOldRunAttempSelected || isUserViewer}
            activeRunTab={activeRunTab}
            pendingRunEnvironmentId={launchRefusal.pendingSelection?.id ?? null}
          />
        ),
    },
    {
      name: RunTabs.Logs,
      children: (
        <RunLogs
          uuid={runSettingsData?.runUUID}
          shouldRefetchLogs={isRunStatusActive}
          runLogsContent={runLogsContent}
          setRunLogsContent={setRunLogsContent}
          selectedRunAttempt={selectedRunAttempt}
          activeRunTab={activeRunTab}
          isAwaitingNewRunLogs={isLaunchInFlight}
        />
      ),
    },
    {
      name: RunTabs.Reports,
      children: (
        <RunReports
          runSettingsData={runSettingsData}
          isRunSettingsLoading={isRunSettingsLoading}
          isRunSettingsError={isRunSettingsError}
          selectedRunAttempt={selectedRunAttempt}
          runStatus={runStatus?.runStatus}
          activeRunTab={activeRunTab}
        />
      ),
    },
    ...(isDownloadsTabEnabled
      ? [
          {
            name: RunTabs.Downloads,
            children: (
              <RunDownloads
                runSettingsData={runSettingsData}
                isRunSettingsLoading={isRunSettingsLoading}
                isRunSettingsError={isRunSettingsError}
                selectedRunAttempt={selectedRunAttempt}
                activeRunTab={activeRunTab}
                onDownloadStateChange={setIsRunDownloading}
              />
            ),
          },
        ]
      : []),
    ...(isTestTabEnabled
      ? [
          {
            name: RunTabs.Test,
            children: <RunTestTab processId={testTabProcessId} runId={runId} />,
          },
        ]
      : []),
    {
      name: RunTabs.Settings,
      children: (
        <Settings
          form={form}
          runSettingsData={runSettingsData}
          activeRunTab={activeRunTab}
        />
      ),
    },
  ];

  return (
    <main className="via-content-wrapper">
      {showDuplicationBanner && (
        <div
          onClick={() => {
            setIsDuplicateRunModalOpen(true);
          }}
          className="ml-3 cursor-pointer">
          <MessageBanner
            title="Info"
            messages="Shared Run Duplication in progress"
            type={BannerType.Info}
          />
        </div>
      )}
      <form
        onSubmit={e => {
          e.preventDefault();
          const target = e.nativeEvent as SubmitEvent;
          const submitter = target.submitter as HTMLButtonElement;
          if (submitter && submitter.name === "saveButton") {
            onSubmit(e);
          }
        }}>
        <LaunchRefusalPanel
          view={launchRefusal.panelView}
          sshEnvironments={sshRunEnvironments}
          onSwitchEnvironment={handleSwitchEnvironment}
          onOpenEnvironmentPicker={handleOpenEnvironmentPicker}
          onDismiss={launchRefusal.dismiss}
        />
        <div className="via-page-heading-wrapper">
          {isRunSettingsLoading ? (
            <TitleLoader />
          ) : (
            <div className="mb-1 mt-3 flex items-center text-xl">
              <div className="mr-1 font-bold">Pipeline:</div>
              <div>
                <RunPipelineLink
                  pipelineId={runSettingsData?.mainPipeline?.id ?? 0}
                  pipelineName={`${runSettingsData?.mainPipeline?.name} (${runSettingsData?.mainPipeline?.version})`}
                  subjectType={runSettingsData?.mainPipeline?.subjectType}
                  linkClassName="inline-flex items-center justify-center rounded-md text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                  textClassName="inline-flex items-center justify-center"
                />
              </div>
              <div className="mx-3 font-bold">|</div>
              <div className="mr-1 font-bold">Run:</div>
              <span
                ref={measureRef}
                className="invisible fixed -left-[9999px] leading-10 text-xl"
                aria-hidden="true">
                {runName}
              </span>
              <input
                type="text"
                ref={runNameInputRef}
                disabled={isAnyOldRunAttempSelected || isUserViewer}
                placeholder="Enter Run Name"
                className={classNames(
                  "mr-1 max-w-80 border-none leading-10 text-gray-900 sm:truncate sm:tracking-tight text-xl md:leading-normal disabled:text-gray-900",
                  !isAnyOldRunAttempSelected || !isUserViewer
                    ? "hover:bg-gray-100 ease-in-out duration-300"
                    : ""
                )}
                style={{
                  width: `${runNameInputWidth}px`,
                  transition: "width 0.2s ease", // Smooth transition
                }}
                onChange={e => setRunName(e.target.value)}
                value={runName}
                onClick={handleEditRunName}
                onBlur={handleBlurRunName}
              />
              {!isAnyOldRunAttempSelected &&
                !isUserViewer &&
                !isEditingRunName && (
                  <button
                    onClick={handleEditRunName}
                    className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
                    title="Edit run name">
                    <PencilIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
            </div>
          )}
          <div className="flex gap-2 items-center">
            {shouldShowPredictedCost && (
              <>
                <span
                  id="run-cost-prediction"
                  className="h-[38px] inline-flex items-center rounded-md bg-white px-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  {formattedPredictedCost}
                </span>
                <Tooltip
                  anchorSelect="#run-cost-prediction"
                  offset={6}
                  place="bottom"
                  render={() => (
                    <>
                      Total predicted cost of the runs based on used memory, CPU
                      and disk. It doesn&apos;t show the actual cost.
                    </>
                  )}
                />
              </>
            )}
            <div className="w-42">
              <SelectComponent
                name="runAttempts"
                placeholder="Run History"
                options={runAttempts}
                isSearchable={false}
                enableClear={false}
                isLoading={
                  isRunAttemptsLoading ||
                  isRunSettingsLoading ||
                  isSaveRunLoading ||
                  isRunInitiationLoading ||
                  isTerminateRunLoading
                }
                value={selectedRunAttempt}
                onChange={handleRunAttemptChange}
                formatOptionLabel={(option, { context }) => {
                  return context === "menu" ? (
                    <div className="flex flex-col items-start">
                      <span>{option?.label}</span>
                      <span className="line-clamp-2 text-xs text-gray-400">
                        {option?.dateCreated
                          ? `created at ${formatDateString(option?.dateCreated ?? "", "LLL")}`
                          : "--"}
                      </span>
                    </div>
                  ) : (
                    option.label
                  );
                }}
              />
            </div>
            {/* Viewers see state, the guard below covers only actions: this badge stays OUTSIDE `!isUserViewer`. */}
            <ExecutionVisibilityBadge
              visibility={runStatus?.executionVisibility}
              reason={runStatus?.executionVisibilityReason}
            />
            {!isUserViewer && (
              <>
                {isPremiumRunButton ? (
                  <button
                    id="premium-run-button"
                    type="button"
                    className="h-[38px] whitespace-nowrap inline-flex justify-center rounded-md px-3 py-2.5 text-sm font-semibold text-white shadow-sm bg-green-600 hover:bg-green-500 focus-visible:outline-green-600"
                    onClick={() => setIsPremiumRunModalOpen(true)}>
                    <PlayIcon className="mr-1 h-5" />
                    Premium Run
                  </button>
                ) : (
                  <ContextButton
                    id="initiate-run-button"
                    onMouseEnter={() => setDisplayWarnings(true)}
                    onMouseLeave={() => setDisplayWarnings(false)}
                    text={getRunStatusText(runActionStatus.text)}
                    className={getRunStatusStyle(runActionStatus.style)}
                    loading={
                      isRunStatusLoading ||
                      isRunInitiationLoading ||
                      isTerminateRunLoading ||
                      isLaunchInFlight
                    }
                    disabled={isInitiateRunDisable || isAnyOldRunAttempSelected}
                    options={
                      isInitiateRunDisable || isAnyOldRunAttempSelected
                        ? []
                        : canonicalRunStatus === RunStatus.NotSubmitted
                          ? INITIATE_NEW_RUN_BUTTON
                          : // Offer a launch exactly when the backend would accept
                            // one. This used to be its own hardcoded list of four
                            // statuses, which left `Unreachable` out: a run whose
                            // poller lost the cluster was offered Terminate and
                            // nothing else, and if its scheduler job was already
                            // gone the Terminate could never succeed either. That
                            // is a dead end with no way back, and the backend guard
                            // was never the thing refusing.
                            canLaunchNewAttempt(canonicalRunStatus)
                            ? INITIATE_RUN_BUTTON
                            : TERMINATE_RUN_BUTTON.map(btn => ({
                                ...btn,
                                disabled: !runStatus?.enableTerminate,
                              }))
                    }
                    onSelect={handleRunAction}
                  />
                )}
                <button
                  data-testid="save-run"
                  type="submit"
                  name="saveButton"
                  className="h-[38px] inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 gap-1 items-center"
                  disabled={isSaveRunLoading || isAnyOldRunAttempSelected}>
                  {isSaveRunLoading ? (
                    <Spinner loading={isSaveRunLoading}>Save</Spinner>
                  ) : (
                    <>
                      <SaveIcon /> Save
                    </>
                  )}
                </button>
              </>
            )}
            {config?.ENABLE_SHARE_BUTTON && (
              <button
                type="button"
                disabled={isRunSettingsLoading || isUserViewer}
                onClick={() => setIsShareRunModalOpen(true)}
                className="h-[38px] bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md inline-flex gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                {isRunSettingsLoading ? (
                  <Spinner loading={isRunSettingsLoading} />
                ) : (
                  <ShareIcon
                    className="-ml-0.5 mr-0.5 h-5 w-5"
                    aria-hidden="true"
                  />
                )}
                Share
              </button>
            )}

            {runContextMenuOptions.length > 0 && (
              <TableContextMenu
                options={runContextMenuOptions}
                onSelect={handleRunAction}
                className="rounded-md border border-indigo-600 px-2 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:border-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              />
            )}
          </div>
        </div>
        {!isRunSettingsLoading && (
          <span className="text-xs text-gray-500">
            Created by {runSettingsData?.owner?.username} on{" "}
            {formatDateString(runSettingsData?.owner?.dateCreated, "LLL")} •
            Last edited on{" "}
            {formatDateString(runSettingsData?.owner?.dateModified, "LLL")}
          </span>
        )}
        <div className="flex items-baseline justify-between">
          <HorizontalTabs
            buttonIdPrefix="run"
            activeTab={activeRunTab}
            onTabChange={setActiveRunTab}
            options={runTabs}
          />
        </div>
        {isInitiateRunDisable && (
          <Tooltip
            anchorSelect="#initiate-run-button"
            offset={6}
            place="bottom"
            render={() => (
              <>
                Waiting for input parameters, output directory and selection of
                active environment
              </>
            )}
          />
        )}
        {unconfirmedTermination && (
          <TerminateWithoutConfirmationModal
            open={true}
            setOpen={open => {
              if (!open) setUnconfirmedTermination(null);
            }}
            refusal={unconfirmedTermination}
            isConfirmLoading={isTerminateRunLoading}
            onConfirm={async () => {
              terminateRun(
                { id: runId, withoutConfirmation: true },
                { onSettled: () => setUnconfirmedTermination(null) }
              );
            }}
          />
        )}
        {isDeleteRunModalOpen && (
          <DeleteRunModal
            open={isDeleteRunModalOpen}
            setOpen={setIsDeleteRunModalOpen}
            runName={runSettingsData?.name}
            runId={runId}
          />
        )}
        {isManageRunAttemptsModalOpen && (
          <ManageRunAttemptsModal
            open={isManageRunAttemptsModalOpen}
            setOpen={setIsManageRunAttemptsModalOpen}
            runId={runId}
          />
        )}
        {isPremiumRunModalOpen && (
          <PremiumRunModal
            open={isPremiumRunModalOpen}
            setOpen={setIsPremiumRunModalOpen}
          />
        )}
        {isChangeProjectModalOpen && (
          <ChangeProjectDuplicateRunModal
            open={isChangeProjectModalOpen}
            setOpen={setIsChangeProjectModalOpen}
            runId={runId}
            mode={RUN_MANAGEMENT_MODALS.CHANGE_PROJECT}
          />
        )}
        {isDuplicateRunModalOpen && (
          <ChangeProjectDuplicateRunModal
            open={isDuplicateRunModalOpen}
            setOpen={setIsDuplicateRunModalOpen}
            runId={runId}
            projectId={runSettingsData?.project?.id}
            mode={RUN_MANAGEMENT_MODALS.DUPLICATE_RUN}
            pipelineId={runSettingsData?.mainPipeline?.id}
          />
        )}
        {isShareRunModalOpen && (
          <ShareRunModal
            open={isShareRunModalOpen}
            setOpen={setIsShareRunModalOpen}
            runId={runId}
            groupId={runSettingsData?.groupId}
            sharedGroupId={runSettingsData?.sharedGroupId}
            permission={runSettingsData?.permission}
          />
        )}
      </form>
      {NavigationBlockerModal}
    </main>
  );
}
